import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {MutationObserver | null} */
  #accountObserver = null;

  /** @type {MutationObserver | null} */
  #loginFormObserver = null;

  /** @type {ShadowRoot | null} */
  #observedLoginFormRoot = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#putEmailSignInFirst();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#accountObserver?.disconnect();
    this.#loginFormObserver?.disconnect();
    this.#accountObserver = null;
    this.#loginFormObserver = null;
    this.#observedLoginFormRoot = null;
  }

  /**
   * Shopify renders the sign-in choices inside nested shadow roots. Always
   * expand all options so Shop recognition cannot hide email, then keep the
   * email form and its consent control above the divider, with Shop below it.
   */
  #putEmailSignInFirst = async () => {
    const account = this.querySelector('shopify-account');
    if (!account) return;

    await customElements.whenDefined('shopify-account');
    if (!this.isConnected || !account.shadowRoot) return;

    const reorder = (shadowRoot) => {
      if (!(shadowRoot instanceof ShadowRoot)) return;

      const showAllSignIns = shadowRoot.querySelector('.login-form__show-logins-button');
      if (showAllSignIns instanceof HTMLButtonElement) {
        showAllSignIns.click();
        return;
      }

      const root = shadowRoot.querySelector('.login-form__root');
      if (!(root instanceof HTMLElement)) return;

      const shop = root.querySelector('slot[name="shop-login"]');
      const divider = root.querySelector('.login-form__divider');
      const email = root.querySelector('.login-form__email-form');
      const consent = root.querySelector('.login-form__marketing-consent');
      if (!shop || !divider || !email || !consent) return;

      const children = [...root.children];
      const alreadyOrdered =
        children[0] === email && children[1] === consent && children[2] === divider && children[3] === shop;
      if (alreadyOrdered) return;

      root.insertBefore(email, divider);
      root.insertBefore(consent, divider);
      root.append(shop);
    };

    const connectLoginForm = () => {
      const loginForm = account.shadowRoot?.querySelector('shopify-login-form');
      const shadowRoot = loginForm?.shadowRoot;
      if (!(shadowRoot instanceof ShadowRoot)) return;

      if (this.#observedLoginFormRoot !== shadowRoot) {
        this.#loginFormObserver?.disconnect();
        this.#observedLoginFormRoot = shadowRoot;
        this.#loginFormObserver = new MutationObserver(() => reorder(shadowRoot));
        this.#loginFormObserver.observe(shadowRoot, { childList: true, subtree: true });
      }

      reorder(shadowRoot);
    };

    await customElements.whenDefined('shopify-login-form');
    if (!this.isConnected) return;

    connectLoginForm();
    this.#accountObserver = new MutationObserver(connectLoginForm);
    this.#accountObserver.observe(account.shadowRoot, { childList: true, subtree: true });
  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
